import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { toast } from 'sonner';
import { ThemeProvider } from '../theme/ThemeContext';
import { initStore } from './store';
import App from './App';
import { setupState, setUIMode, setMountedFromPlugin } from './recorderSlice';
import getMiddleware from './middleware';
import { EventEmitter, EventListener, EventMap } from './EventEmitter';
import { getSettings } from '../services/authService';
import { setToken } from '../lib/localStorageHandler';
import { setHeader } from '../lib/baseAxios';
import { cleanup } from './Recorder/utils/streamUtils';
import { cleanupTitleNotifications } from './utils/titleNotification';

const getRecorderComponent = (store: ReturnType<typeof initStore>) => {
  const Recorder = () => {
    return (
      <ThemeProvider>
        <Provider store={store}>
          <App />
        </Provider>
      </ThemeProvider>
    );
  };
  return Recorder;
};

export default class ScreenAppRecorder {
  apiKey: string;

  parentElementSelector: string;

  teamId: string;

  folderId: string;

  settings: Parameters<typeof setupState>[0]['settings'];

  uiMode: 'fullscreen' | 'popup';

  isPlugin: boolean;

  private eventEmitter: EventEmitter;

  private root: ReturnType<typeof createRoot> | null = null;

  constructor({
    apiKey,
    parentElementSelector,
    teamId,
    folderId,
    settings,
    uiMode = 'fullscreen',
    isPlugin = false,
  }: {
    apiKey: string;
    parentElementSelector?: string;
    teamId: string;
    folderId: string;
    settings?: Parameters<typeof setupState>[0]['settings'];
    uiMode?: 'fullscreen' | 'popup';
    isPlugin?: boolean;
  }) {
    this.apiKey = apiKey;
    this.parentElementSelector = parentElementSelector || '#screenapp-recorder';
    this.teamId = teamId;
    this.folderId = folderId;
    this.settings = settings;
    this.uiMode = uiMode;
    this.isPlugin = isPlugin;
    this.eventEmitter = new EventEmitter();
    this.eventEmitter.on('uploaded', () => {
      this.destroy();
    });
    this.eventEmitter.on('canceled', () => {
      this.destroy();
    });
    this.eventEmitter.on('error', (event) => {
      console.log('ScreenAppRecorder received error event:', event.detail);

      // Show persistent error message that waits for user interaction
      this.showErrorMessage(event.detail.message);

      // Delay cleanup to allow user to see the error
      setTimeout(() => {
        this.destroy();
      }, 100);
    });
  }

  showErrorMessage(message: string) {
    toast.error(message);
  }

  destroy() {
    // Clean up all streams and permissions first
    cleanup();

    // Clean up any active title notifications
    cleanupTitleNotifications();

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  async initialize() {
    const store = initStore([getMiddleware(this.eventEmitter)]);
    if (!this.parentElementSelector) {
      document.body.insertAdjacentHTML(
        'beforeend',
        `<div id="${this.parentElementSelector.replace('#', '')}"></div>`,
      );
    }
    const parentElement = document.querySelector(this.parentElementSelector);
    if (!parentElement) {
      throw new Error('Parent element not found');
    }

    // Retrieve settings from localStorage
    const localStorageSettings = localStorage.getItem('recorderSettings');
    const parsedSettings = localStorageSettings
      ? JSON.parse(localStorageSettings)
      : {};
    let audioSettings = {};
    let videoSettings = {};
    if (!this.isPlugin) {
      // Don't fetch settings again in plugin mode
      const {
        audioSettings: audioSettingsFromServer,
        videoSettings: videoSettingsFromServer,
      } = await getSettings('recorder');
      audioSettings = audioSettingsFromServer;
      videoSettings = videoSettingsFromServer;
    } else {
      // XXX: Set token in memory but don't store it in localStorage
      console.log('Setting token in memory:', this.apiKey);
      setToken(this.apiKey, false);
      setHeader(this.apiKey);
    }
    const constructorAudioSettings = this.settings?.audioSettings;
    this.settings = {
      audioSettings: {
        ...audioSettings,
        ...parsedSettings?.audioSettings, // Merge with localStorage settings
        ...constructorAudioSettings, // Constructor settings take highest priority
      },
      videoSettings: {
        ...videoSettings,
        ...parsedSettings?.videoSettings, // Merge with localStorage settings
        ...this.settings?.videoSettings, // Constructor settings take highest priority
      },
    };
    console.log('🔧 RecorderV3 settings merge:', {
      serverMicAudio: (audioSettings as any)?.microphoneAudio,
      localStorageMicAudio: parsedSettings?.audioSettings?.microphoneAudio,
      constructorMicAudio: constructorAudioSettings?.microphoneAudio,
      finalMicAudio: this.settings.audioSettings?.microphoneAudio,
    });
    await store.dispatch(
      setupState({
        parentElementSelector: this.parentElementSelector,
        uploadState: { teamId: this.teamId, folderId: this.folderId },
        settings: this.settings,
      }),
    );

    // Set the UI mode (fullscreen or popup)
    store.dispatch(setUIMode(this.uiMode));

    // Set that this recorder is mounted from the plugin only if it's actually from the plugin
    store.dispatch(setMountedFromPlugin(this.isPlugin));

    const RecorderComponent = getRecorderComponent(store);
    this.root = createRoot(parentElement);
    this.root.render(<RecorderComponent />);
    this.eventEmitter.emit('ready', {
      teamId: this.teamId,
      folderId: this.folderId,
    });
  }

  on<K extends keyof EventMap>(type: K, listener: EventListener<K>) {
    this.eventEmitter.on(type, listener);
  }

  off<K extends keyof EventMap>(type: K, listener: EventListener<K>) {
    this.eventEmitter.off(type, listener);
  }
}
